import React from 'react';
import {
  Building2,
  CalendarDays,
  FolderKanban,
  Landmark,
  LayoutGrid,
  RefreshCw,
  Scale,
} from 'lucide-react';

const PALETTE = {
  navy: '#0F2742',
  green: '#1F8A70',
  greenSoft: '#E8F5F2',
  gold: '#C8A45C',
  grayBg: '#F7F9FB',
  border: '#DCE7E3',
  grayText: '#6B7280',
};

const hasMojibake = (value = '') => /[ØÙ\uFFFD]/.test(value);

const safeValue = (value, fallback) => {
  if (!value || hasMojibake(value)) {
    return fallback;
  }

  return value;
};

const STATUS_STYLES = {
  'قيد النظر': { bg: '#E8F5F2', color: '#1F8A70', border: '#BFE4DA' },
  'قيد المتابعة': { bg: '#E8F5F2', color: '#1F8A70', border: '#BFE4DA' },
  'قيد المراجعة': { bg: '#FBF5E8', color: '#C8A45C', border: '#E6D1A2' },
  'مغلقة': { bg: '#EEF2F7', color: '#64748B', border: '#D7DFEA' },
  default: { bg: '#EEF5F3', color: '#1F8A70', border: '#CBE3DC' },
};

const META_ITEMS = [
  { key: 'case_number', label: 'رقم الملف', icon: FolderKanban, fallback: 'WZ-2024-001' },
  { key: 'type', label: 'نوع القضية / المعاملة', icon: Scale, fallback: 'قضية عمالية' },
  { key: 'court_name', label: 'الجهة المختصة', icon: Landmark, fallback: 'المحكمة العمالية' },
  { key: 'department', label: 'الدائرة / القسم', icon: LayoutGrid, fallback: 'الدائرة الثالثة' },
  { key: 'created_at', label: 'تاريخ الإضافة', icon: CalendarDays, fallback: 'اليوم' },
  { key: 'updated_at', label: 'آخر تحديث', icon: RefreshCw, fallback: 'اليوم' },
  { key: 'status', label: 'الحالة', icon: Building2, fallback: 'قيد النظر', isStatus: true },
];

const CaseHeader = ({ caseData, isArabic = true }) => {
  const rawStatus = safeValue(caseData?.status, 'قيد النظر');
  const statusValue = rawStatus === 'قيد المتابعة' ? 'قيد النظر' : rawStatus;
  const statusStyle = STATUS_STYLES[statusValue] || STATUS_STYLES.default;

  return (
    <section
      style={{
        background: 'rgba(255,255,255,0.94)',
        border: `1px solid ${PALETTE.border}`,
        borderRadius: 28,
        padding: '28px 30px',
        boxShadow: '0 24px 56px rgba(15, 39, 66, 0.08)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          insetInlineStart: '-6%',
          top: '-24%',
          width: 320,
          height: 320,
          background: 'radial-gradient(circle, rgba(31,138,112,0.12), transparent 68%)',
          pointerEvents: 'none',
        }}
      />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 12,
          position: 'relative',
          zIndex: 1,
        }}
      >
        {META_ITEMS.map(({ key, label, icon: Icon, fallback, isStatus }) => {
          const value = safeValue(caseData?.[key], fallback);
          const itemStatusStyle = isStatus
            ? STATUS_STYLES[value] || STATUS_STYLES.default
            : null;

          return (
            <div
              key={key}
              style={{
              background: '#FFFFFF',
              border: `1px solid ${PALETTE.border}`,
              borderRadius: 20,
              padding: '14px 16px',
              boxShadow: '0 10px 24px rgba(15, 39, 66, 0.05)',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              minHeight: 92,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                color: PALETTE.grayText,
                fontSize: 12,
                fontWeight: 800,
              }}
            >
              <span
                style={{
                  width: 30,
                  height: 30,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 10,
                  background: PALETTE.grayBg,
                  border: `1px solid ${PALETTE.border}`,
                  color: PALETTE.green,
                  flexShrink: 0,
                }}
              >
                  <Icon size={16} />
                </span>
                {label}
              </div>

              <div
                style={{
                  color: isStatus ? itemStatusStyle.color : PALETTE.navy,
                  background: isStatus ? itemStatusStyle.bg : 'transparent',
                  border: isStatus ? `1px solid ${itemStatusStyle.border}` : 'none',
                  borderRadius: isStatus ? 999 : 0,
                  padding: isStatus ? '7px 12px' : 0,
                  alignSelf: 'flex-start',
                  fontSize: 13,
                  fontWeight: 900,
                  lineHeight: 1.6,
                }}
              >
                {value}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default CaseHeader;
